//mushroom-inoculations-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useMemo } from "react";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  GridRowParams,
  GridRowClassNameParams,
  useGridApiRef,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridRowId,
} from "@mui/x-data-grid";
import {
  IconButton,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingBackdrop from "../components/loading-backdrop";
import Page from "../components/page";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { ActiveFilterControls } from "../components/active-filter-controls";
import { getSearchParamNumber } from "../utils/history";
import { useHistory } from "react-router-dom";

type MushroomBatchType = "culture" | "spawn" | "substrate";
type MushroomStage =
  | "agar"
  | "liquid_culture"
  | "grain_spawn"
  | "bulk_spawn"
  | "fruiting_block";

interface MushroomBatch {
  id: number;
  code: string;
  type: MushroomBatchType;
  strainName: string;
  recipeName: string | null;
  roomName: string | null;
  parentCode: string | null;
  inoculatedAt: string;
  notes: string | null;
  archivedAt: string | null;
}

const inoculationsGridStateStorageKey = "mushroomInoculationsGridState";
const inoculationsGridDensityStorageKey = "mushroomInoculationsGridDensity";

const typeLabel = (t: MushroomBatchType) => {
  switch (t) {
    case "culture":
      return "Culture";
    case "spawn":
      return "Spawn";
    case "substrate":
      return "Substrate";
    default:
      return t;
  }
};

const InoculationsDataGrid = React.memo(
  (props: {
    batches: MushroomBatch[] | null;
    onSelectionChange: (params: GridRowSelectionModel) => void;
    initialPage?: number;
    onPaginationModelChange: (m: GridPaginationModel) => void;
    onClickRow: (id: GridRowId) => void;
  }) => {
    const csvFileName = React.useMemo(() => {
      const now = DateTime.local();

      return `SeedLeaf Mushroom Inoculations - ${now.toFormat("MMM dd, yyyy")}`;
    }, []);

    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(inoculationsGridStateStorageKey);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;
            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }
        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 25 },
          };
        }

        localStorage.setItem(
          inoculationsGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(inoculationsGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(inoculationsGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "inoculatedAt", sort: "desc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        inoculationsGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const columns: GridColDef[] = [
      {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        hideable: false,
        headerName: "Checkbox",
      },
      {
        field: "archived",
        headerName: "",
        width: 35,
        minWidth: 35,
        sortable: false,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        disableExport: true,
        renderCell: (params) =>
          params.row.archivedAt ? (
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          ) : null,
      },
      {
        field: "code",
        headerName: "Code",
        width: 140,
      },
      {
        field: "type",
        headerName: "Type",
        width: 140,
        renderCell: (params) => typeLabel(params.row.type),
      },
      {
        field: "strainName",
        headerName: "Strain",
        width: 220,
      },
      {
        field: "recipeName",
        headerName: "Recipe",
        width: 220,
      },
      {
        field: "roomName",
        headerName: "Room",
        width: 200,
      },
      {
        field: "parentCode",
        headerName: "Parent",
        width: 140,
      },
      {
        field: "inoculatedAt",
        headerName: "Inoculated",
        width: 160,
      },
      {
        field: "notes",
        headerName: "Notes",
        width: 300,
      },
    ];

    if (!props.batches) return null;

    if (!initialState) {
      return <CircularProgress />;
    }

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.batches.map((b) => ({
          id: b.id,
          code: b.code,
          type: b.type,
          strainName: b.strainName,
          recipeName: b.recipeName,
          roomName: b.roomName,
          parentCode: b.parentCode,
          inoculatedAt: b.inoculatedAt,
          notes: b.notes,
          archivedAt: b.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "inoculatedAt",
                sort: "desc",
              },
            ],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        showToolbar
        slots={{
          toolbar: GridToolbar,
        }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(inoculationsGridDensityStorageKey, d);
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) => {
          props.onClickRow(params.id);
        }}
      />
    );
  },
);

export default function MushroomInoculationsPage() {
  const [batches, setBatches] = useState<MushroomBatch[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  const showArchivedCheckedStorageKey =
    "mushroomInoculationsShowArchivedChecked";
  const showActiveCheckedStorageKey = "mushroomInoculationsShowActiveChecked";
  const mushroomStrainsStorageKey = "mushroomStrains";

  const [strainOptions, setStrainOptions] = useState<
    { name: string; timing?: any }[]
  >([]);

  const loadStrainOptions = () => {
    const raw = window.localStorage.getItem(mushroomStrainsStorageKey);

    if (!raw) {
      setStrainOptions([
        { name: "Blue Oyster", timing: null },
        { name: "Lion’s Mane", timing: null },
      ]);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as any[];

      const next = (parsed || [])
        .filter((s) => !!s?.name)
        .map((s) => ({
          name: s.name,
          timing: s,
        }));

      setStrainOptions(
        next.length
          ? next
          : [
              { name: "Blue Oyster", timing: null },
              { name: "Lion’s Mane", timing: null },
            ],
      );
    } catch {
      window.localStorage.removeItem(mushroomStrainsStorageKey);
      setStrainOptions([
        { name: "Blue Oyster", timing: null },
        { name: "Lion’s Mane", timing: null },
      ]);
    }
  };

  useEffect(() => {
    loadStrainOptions();
  }, []);

  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const [tab, setTab] = useState<MushroomBatchType>("substrate");

  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState<MushroomBatchType>("substrate");
  const [addCode, setAddCode] = useState("");
  const [addStrainName, setAddStrainName] = useState("");
  const [addRecipeName, setAddRecipeName] = useState("");
  const [addRoomName, setAddRoomName] = useState("");
  const [addParentCode, setAddParentCode] = useState("");
  const [addInoculatedAt, setAddInoculatedAt] = useState(
    DateTime.local().toFormat("yyyy-LL-dd"),
  );
  const [addNotes, setAddNotes] = useState("");
  const [addStage, setAddStage] = useState<MushroomStage>("fruiting_block");

  const history = useHistory();
  let initialPage = getSearchParamNumber(history, "page", 0);

  const defaultTiming = {
    daysAgarPlate: 14,
    daysLiquidCulture: 10,
    daysGrainSpawn: 0,
    daysBulkSpawn: 0,
    daysFruitingBlockColonization: 14,
    daysCasing: 0,
    daysFruitingToFirstHarvest: 7,
    harvestWindowDays: 5,
  };

  const getTimingForStrainName = (name: string) => {
    const found = strainOptions.find(
      (s) => s.name.toLowerCase() === (name || "").trim().toLowerCase(),
    );

    if (found?.timing) {
      return {
        daysAgarPlate:
          found.timing.daysAgarPlate ??
          found.timing.daysAgarToGrain ??
          defaultTiming.daysAgarPlate,
        daysLiquidCulture:
          found.timing.daysLiquidCulture ??
          found.timing.daysLCToGrain ??
          defaultTiming.daysLiquidCulture,
        daysGrainSpawn:
          found.timing.daysGrainSpawn ??
          found.timing.daysGrainToBulk ??
          defaultTiming.daysGrainSpawn,
        daysBulkSpawn:
          found.timing.daysBulkSpawn ?? defaultTiming.daysBulkSpawn,
        daysFruitingBlockColonization:
          found.timing.daysFruitingBlockColonization ??
          found.timing.daysSpawnToBlock ??
          defaultTiming.daysFruitingBlockColonization,
        daysCasing: found.timing.daysCasing ?? defaultTiming.daysCasing,
        daysFruitingToFirstHarvest:
          found.timing.daysFruitingToFirstHarvest ??
          found.timing.daysFruitingToHarvest ??
          defaultTiming.daysFruitingToFirstHarvest,
        harvestWindowDays:
          found.timing.harvestWindowDays ?? defaultTiming.harvestWindowDays,
      };
    }

    return defaultTiming;
  };

  useEffect(() => {
    if (!addOpen) return;

    if (!addStrainName && strainOptions.length > 0) {
      setAddStrainName(strainOptions[0].name);
    }
  }, [addOpen, addStrainName, strainOptions]);

  const estimate = useMemo(() => {
    const addInoculatedDate = DateTime.fromFormat(
      addInoculatedAt,
      "yyyy-LL-dd",
    );

    const timing = getTimingForStrainName(addStrainName);

    const estimatedDaysFromStage =
      addStage === "agar"
        ? timing.daysAgarPlate +
          timing.daysGrainSpawn +
          timing.daysBulkSpawn +
          timing.daysFruitingBlockColonization +
          timing.daysCasing +
          timing.daysFruitingToFirstHarvest
        : addStage === "liquid_culture"
          ? timing.daysLiquidCulture +
            timing.daysGrainSpawn +
            timing.daysBulkSpawn +
            timing.daysFruitingBlockColonization +
            timing.daysCasing +
            timing.daysFruitingToFirstHarvest
          : addStage === "grain_spawn"
            ? timing.daysGrainSpawn +
              timing.daysBulkSpawn +
              timing.daysFruitingBlockColonization +
              timing.daysCasing +
              timing.daysFruitingToFirstHarvest
            : addStage === "bulk_spawn"
              ? timing.daysBulkSpawn +
                timing.daysFruitingBlockColonization +
                timing.daysCasing +
                timing.daysFruitingToFirstHarvest
              : timing.daysFruitingBlockColonization +
                timing.daysCasing +
                timing.daysFruitingToFirstHarvest;

    const estimatedFirstHarvestDate = addInoculatedDate.isValid
      ? addInoculatedDate.plus({ days: estimatedDaysFromStage })
      : null;

    const estimatedRangeEnd = estimatedFirstHarvestDate
      ? estimatedFirstHarvestDate.plus({ days: timing.harvestWindowDays })
      : null;

    return { estimatedFirstHarvestDate, estimatedRangeEnd };
  }, [addInoculatedAt, addStrainName, addStage, strainOptions]);

  useEffect(() => {
    setBatches([
      {
        id: 1,
        code: "SUB-0001",
        type: "substrate",
        strainName: "Blue Oyster",
        recipeName: "Masters Mix (50/50)",
        roomName: "Incubation Shelf 1",
        parentCode: "SPN-0003",
        inoculatedAt: DateTime.local()
          .minus({ days: 2 })
          .toFormat("yyyy-LL-dd"),
        notes: null,
        archivedAt: null,
      },
      {
        id: 2,
        code: "SPN-0003",
        type: "spawn",
        strainName: "Blue Oyster",
        recipeName: "Grain Spawn (standard)",
        roomName: "Incubation Shelf 1",
        parentCode: "CUL-0008",
        inoculatedAt: DateTime.local()
          .minus({ days: 10 })
          .toFormat("yyyy-LL-dd"),
        notes: null,
        archivedAt: null,
      },
      {
        id: 3,
        code: "CUL-0008",
        type: "culture",
        strainName: "Blue Oyster",
        recipeName: "Agar Plate (basic)",
        roomName: null,
        parentCode: null,
        inoculatedAt: DateTime.local()
          .minus({ days: 21 })
          .toFormat("yyyy-LL-dd"),
        notes: null,
        archivedAt: null,
      },
    ]);
  }, []);

  const handleSelectionChange = (m: GridRowSelectionModel) => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids.length ? [ids[0]] : []);
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const onClickRow = (id: GridRowId) => {
    // Placeholder: later we can route to /mushroom/home/inoculations/:id
    return;
  };

  const handleClickAdd = () => {
    loadStrainOptions();
    setAddType(tab);
    setAddCode("");
    setAddStrainName("");
    setAddRecipeName("");
    setAddRoomName("");
    setAddParentCode("");
    setAddInoculatedAt(DateTime.local().toFormat("yyyy-LL-dd"));
    setAddNotes("");
    setAddOpen(true);
    setAddStage(
      tab === "culture"
        ? "agar"
        : tab === "spawn"
          ? "grain_spawn"
          : "fruiting_block",
    );
    setAddStrainName(strainOptions?.[0]?.name || "");
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = async () => {
    if (!batches) return;

    if (!addCode.trim()) {
      return;
    }

    setBusy(true);

    try {
      const maxId = batches.reduce((m, b) => Math.max(m, b.id), 0);

      const next: MushroomBatch = {
        id: maxId + 1,
        code: addCode.trim(),
        type: addType,
        strainName: addStrainName.trim() ? addStrainName.trim() : "Unknown",
        recipeName: addRecipeName.trim() ? addRecipeName.trim() : null,
        roomName: addRoomName.trim() ? addRoomName.trim() : null,
        parentCode: addParentCode.trim() ? addParentCode.trim() : null,
        inoculatedAt: addInoculatedAt,
        notes: addNotes.trim() ? addNotes.trim() : null,
        archivedAt: null,
      };

      setBatches([next, ...batches]);
      setAddOpen(false);
    } finally {
      setBusy(false);
    }
  };

  const visibleBatches = batches?.filter((b) => b.type === tab) ?? null;

  return (
    <Page title="Inoculations">
      {batches && (
        <>
          <Box sx={{ mb: 2 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab value="culture" label="Culture" />
              <Tab value="spawn" label="Spawn" />
              <Tab value="substrate" label="Substrate" />
            </Tabs>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 2,
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleClickAdd}
              disabled={busy}
            >
              Add
            </Button>

            <IconButton
              aria-label="delete"
              disabled={busy || selected?.length === 0}
              onClick={() => {
                return;
              }}
              size="large"
            >
              <DeleteIcon />
            </IconButton>

            <ActiveFilterControls
              showActiveChecked={showActiveChecked}
              showArchivedChecked={showArchivedChecked}
              onChangeShowActive={handleShowActiveChanged}
              onChangeShowArchived={handleShowArchivedChanged}
            />
          </Box>

          <Box sx={{ height: 750, width: "100%", pt: 2 }}>
            <InoculationsDataGrid
              batches={
                visibleBatches
                  ? visibleBatches.filter(
                      (b) =>
                        (showActiveChecked && b.archivedAt === null) ||
                        (showArchivedChecked && b.archivedAt !== null),
                    )
                  : null
              }
              onSelectionChange={handleSelectionChange}
              initialPage={initialPage}
              onPaginationModelChange={(m) => {
                const params = new URLSearchParams(`page=${m.page}`);
                history.replace({
                  search: params.toString(),
                });
              }}
              onClickRow={onClickRow}
            />
          </Box>

          <Dialog
            open={addOpen}
            onClose={handleCloseAdd}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Add Inoculation</DialogTitle>
            <DialogContent>
              <Box
                sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}
              >
                <TextField
                  label="Type"
                  value={addType}
                  onChange={(e) =>
                    setAddType(e.target.value as MushroomBatchType)
                  }
                  select
                  fullWidth
                >
                  <MenuItem value="culture">Culture</MenuItem>
                  <MenuItem value="spawn">Spawn</MenuItem>
                  <MenuItem value="substrate">Substrate</MenuItem>
                </TextField>

                <TextField
                  label="Stage"
                  value={addStage}
                  onChange={(e) => setAddStage(e.target.value as MushroomStage)}
                  select
                  fullWidth
                >
                  <MenuItem value="agar">Agar / Plate</MenuItem>
                  <MenuItem value="liquid_culture">Liquid Culture</MenuItem>
                  <MenuItem value="grain_spawn">Grain Spawn</MenuItem>
                  <MenuItem value="bulk_spawn">Bulk Spawn</MenuItem>
                  <MenuItem value="fruiting_block">Fruiting Block</MenuItem>
                </TextField>

                <Box sx={{ fontSize: 14, opacity: 0.85 }}>
                  Estimated first harvest:{" "}
                  {estimate.estimatedFirstHarvestDate
                    ? `${estimate.estimatedFirstHarvestDate.toFormat("MMM dd")} - ${estimate.estimatedRangeEnd?.toFormat("MMM dd")}`
                    : "—"}
                </Box>

                <TextField
                  label="Code"
                  value={addCode}
                  onChange={(e) => setAddCode(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Strain"
                  value={addStrainName}
                  onChange={(e) => setAddStrainName(e.target.value)}
                  select
                  fullWidth
                >
                  {strainOptions.map((s) => (
                    <MenuItem key={s.name} value={s.name}>
                      {s.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Recipe"
                  value={addRecipeName}
                  onChange={(e) => setAddRecipeName(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Room"
                  value={addRoomName}
                  onChange={(e) => setAddRoomName(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Parent Code"
                  value={addParentCode}
                  onChange={(e) => setAddParentCode(e.target.value)}
                  fullWidth
                />

                <TextField
                  label="Inoculated Date"
                  type="date"
                  value={addInoculatedAt}
                  onChange={(e) => setAddInoculatedAt(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  label="Notes"
                  value={addNotes}
                  onChange={(e) => setAddNotes(e.target.value)}
                  fullWidth
                  multiline
                  minRows={3}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseAdd} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAdd}
                color="primary"
                variant="contained"
                disabled={busy || !addCode.trim()}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
